import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import Select, { components } from 'react-select';
import PropertiesStore from './stores/PropertiesStore';

const CustomControl = ({ children, ...props }) => {
  return (
    <div title={props.title}>
      <components.Control {...props}>{children}</components.Control>
    </div>
  );
};

function GlobalPropertiesSelect({
  selectedDatabase,
  bearerToken,
  refreshBearerToken,
  propertiesStore,
  useRpin
}) {
  const properties = propertiesStore.properties;
  const selectedProperty = propertiesStore.selectedProperty;

  const label = (property, useRpin) => {
    const { name, identifier } = property;
    if (useRpin) {
      return name ? `${name} - ${identifier}` : identifier;
    }
    return name;
  };

  // The properties to display in the select.
  const options = useMemo(
    () =>
      properties
        ? properties
            .filter((property) => {
              // If the property is selected then don't filter.
              // Otherwise, filter if the property has been soft deleted.
              if (propertiesStore.selectProperty.id === property.id)
                return true;
              return !property.deleted_at;
            })
            .map((property) => {
              return {
                value: property.id,
                label: label(property, useRpin),
                identifier: property.identifier
              };
            })
        : [],
    [properties, propertiesStore.selectProperty.id]
  );

  const value = options.find((property) => {
    return property.value === selectedProperty?.id;
  });

  const controlTitle = propertiesStore.loading
    ? 'loading...'
    : selectedProperty
    ? label(selectedProperty, useRpin)
    : undefined;

  return (
    <Select
      components={{
        Control: (props) => <CustomControl {...props} title={controlTitle} />
      }}
      options={options}
      isLoading={propertiesStore.loading}
      onChange={(option) =>
        propertiesStore.selectProperty(
          option.value,
          selectedDatabase,
          bearerToken,
          refreshBearerToken
        )
      }
      value={value}
      filterOption={(option, inputValue) => {
        const inputValueLowerCase = inputValue.toLowerCase();
        return (
          option.label.toLowerCase().includes(inputValueLowerCase) ||
          (option.data.identifier &&
            option.data.identifier.toLowerCase().includes(inputValueLowerCase))
        );
      }}
      styles={{
        menu: (provided) => ({
          ...provided,
          width: 'max-content',
          minWidth: '100%'
        })
      }}
    />
  );
}

GlobalPropertiesSelect.propTypes = {
  // Authentication
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  // Properties
  propertiesStore: PropTypes.instanceOf(PropertiesStore),

  // Misc
  useRpin: PropTypes.bool
};

export default observer(GlobalPropertiesSelect);
